import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchLink", "searchContainer", "searchInput", "dropdown",
    "selectedChip", "selectedName", "cityInput", "cityLabel"
  ]
  static values = {
    url: String,
    pillCityIds: Array
  }

  connect() {
    this._debounceTimer = null
    this._pillClickHandler = () => this._clearChip()
    this.element.querySelectorAll('.city-pill').forEach(pill => {
      pill.addEventListener('click', this._pillClickHandler)
    })
  }

  disconnect() {
    this.element.querySelectorAll('.city-pill').forEach(pill => {
      pill.removeEventListener('click', this._pillClickHandler)
    })
    clearTimeout(this._debounceTimer)
  }

  showSearch() {
    this.searchLinkTarget.classList.add('hidden')
    this.searchContainerTarget.classList.remove('hidden')
    this.searchInputTarget.focus()
  }

  onInput() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._fetchResults(), 250)
  }

  onBlur() {
    setTimeout(() => this._hideDropdown(), 150)
  }

  onDropdownSelect(event) {
    const btn = event.currentTarget
    this._selectCity(btn.dataset.cityId, btn.dataset.cityName, btn.dataset.cityProvince)
  }

  clearSelection() {
    this.cityInputTarget.value = ''
    this.cityLabelTarget.textContent = 'No location selected'
    this._clearChip()
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  _clearChip() {
    this.selectedChipTarget.classList.add('hidden')
    this.searchContainerTarget.classList.add('hidden')
    this.searchLinkTarget.classList.remove('hidden')
    this.searchInputTarget.value = ''
    this._hideDropdown()
  }

  async _fetchResults() {
    const query = this.searchInputTarget.value.trim()
    if (query.length < 2) {
      this._hideDropdown()
      return
    }

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('q', query)
      const response = await fetch(url.toString(), {
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
      if (!response.ok) { this._hideDropdown(); return }
      const cities = await response.json()
      this._renderDropdown(cities)
    } catch {
      this._hideDropdown()
    }
  }

  _renderDropdown(cities) {
    if (cities.length === 0) {
      this.dropdownTarget.innerHTML = '<div class="px-4 py-3 text-sm text-gray-500">No cities found</div>'
    } else {
      this.dropdownTarget.innerHTML = cities.map(city => `
        <button type="button"
                class="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors"
                data-city-id="${city.id}"
                data-city-name="${city.name}"
                data-city-province="${city.province_name}"
                data-action="mousedown->wizard-location-search#onDropdownSelect">
          <span class="font-medium text-navy-900">${city.name}</span>
          <span class="text-gray-400 ml-1">${city.province_name}</span>
        </button>
      `).join('')
    }
    this.dropdownTarget.classList.remove('hidden')
  }

  _selectCity(id, name, provinceName) {
    this._hideDropdown()
    const isPillCity = this.pillCityIdsValue.map(String).includes(String(id))

    if (isPillCity) {
      const pill = this.element.querySelector(`.city-pill[data-city-id="${id}"]`)
      if (pill) pill.click()
      this.searchContainerTarget.classList.add('hidden')
      this.searchLinkTarget.classList.remove('hidden')
      this.searchInputTarget.value = ''
    } else {
      this.cityInputTarget.value = id
      this.cityLabelTarget.textContent = `${name}, ${provinceName}`
      this.selectedNameTarget.textContent = `${name}, ${provinceName}`
      this.selectedChipTarget.classList.remove('hidden')
      this.searchContainerTarget.classList.add('hidden')
      this.searchLinkTarget.classList.remove('hidden')
      this.searchInputTarget.value = ''
    }
  }

  _hideDropdown() {
    this.dropdownTarget.classList.add('hidden')
  }
}
